import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { FieldWrapper } from '@progress/kendo-react-form'
import {
  Input,
  MaskedTextBox,
  NumericTextBox,
  Checkbox,
  ColorPicker,
  Switch,
  RadioGroup,
  Slider,
  SliderLabel,
  RangeSlider,
  TextArea,
  Rating,
  TextBox,
  InputPrefix,
} from '@progress/kendo-react-inputs'
import {
  DatePicker,
  TimePicker,
  DateTimePicker,
  DateRangePicker,
  DateInput,
  ToggleButton,
} from '@progress/kendo-react-dateinputs'
import { Label, Error, Hint, FloatingLabel } from '@progress/kendo-react-labels'
import { Upload } from '@progress/kendo-react-upload'
import {
  DropDownList,
  AutoComplete,
  MultiSelect,
  ComboBox,
  MultiColumnComboBox,
  DropDownTree,
  DropDownListFilterChangeEvent,
  SearchBar,
} from '@progress/kendo-react-dropdowns'
import { processTreeData, expandedState } from './TreeDataOperations'
import { filterBy } from '@progress/kendo-data-query'
import { FaPlusCircle, FaTimesCircle, FaCheckCircle } from 'react-icons/fa'

export const FormTextBoxPassword = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    placeholder,
    defaultText,
    passMatch,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  return (
    <FieldWrapper>
      <Label
        style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        optional={optional}
      >
        {label}
      </Label>
      <div
        className={'k-form-field-wrap'}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <TextBox
          placeholder={placeholder}
          defaultValue={defaultText}
          valid={valid}
          type={type}
          id={id}
          disabled={disabled}
          ariaDescribedBy={`${hintId} ${errorId}`}
          {...others}
          prefix={() => (
            <>
              <InputPrefix>
                {passMatch ? (
                  <FaCheckCircle
                    style={{
                      color: 'rgb(174, 199, 65)',
                      fontSize: '24pt',
                      paddingLeft: '1rem',
                    }}
                  />
                ) : (
                  <FaTimesCircle
                    style={{
                      color: 'rgb(231 130 130)',
                      fontSize: '24pt',
                      paddingLeft: '1rem',
                    }}
                  />
                )}
              </InputPrefix>
            </>
          )}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </div>
    </FieldWrapper>
  )
}
export const FormInput = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    placeholder,
    defaultText,
    hasButton,
    buttonText,
    buttonAction,
    defaultValue,
    value,
    onChange,
    x,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  const handleAction = (event) => {
    event.preventDefault()
    buttonAction()
  }

  return (
    <FieldWrapper>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Label
          style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
          optional={optional}
        >
          {label}
        </Label>
        {hasButton && (
          <button
            onClick={handleAction}
            style={{
              border: 0,
              backgroundColor: 'rgb(148 209 231)',
              color: 'rgb(36 60 69)',
              borderRadius: '1rem',
              fontSize: '9pt',
              padding: '0 1rem 0 1rem',
              marginBottom: '5px',
              fontWeight: '500',
            }}
          >
            {buttonText}
          </button>
        )}
      </div>
      <div
        className={'k-form-field-wrap'}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          {...others}
          placeholder={placeholder}
          valid={valid}
          type={type}
          id={id}
          value={value !== undefined ? value : (x || defaultValue || '')}
          onChange={onChange}
          disabled={disabled}
          ariaDescribedBy={`${hintId} ${errorId}`}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </div>
    </FieldWrapper>
  )
}
export const FormInputPassword = (fieldRenderProps) => {
  const {
    validationMessage,
    passLabel,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    placeholder,
    defaultText,
    passMatch,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  return (
    <FieldWrapper>
      <Label
        style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        optional={optional}
      >
        {label}
      </Label>
      <div
        className={'k-form-field-wrap'}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          style={{ border: passMatch ? '1px solid green' : '1px solid red' }}
          placeholder={placeholder}
          defaultValue={defaultText}
          valid={valid}
          type={type}
          id={id}
          disabled={disabled}
          ariaDescribedBy={`${hintId} ${errorId}`}
          {...others}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        <p style={{ color: 'red' }}>{passLabel}</p>
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </div>
    </FieldWrapper>
  )
}
export const FormRadioGroup = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    id,
    label,
    valid,
    disabled,
    hint,
    visited,
    modified,
    ...others
  } = fieldRenderProps
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <RadioGroup
        ariaDescribedBy={`${hintId} ${errorId}`}
        ariaLabelledBy={labelId}
        valid={valid}
        disabled={disabled}
        ref={editorRef}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormNumericTextBox = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    value,
    defaultValue,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  return (
    <FieldWrapper>
      <Label
        style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <NumericTextBox
        ariaDescribedBy={`${hintId} ${errorId}`}
        valid={valid}
        id={id}
        disabled={disabled}
        value={value}
        defaultValue={defaultValue}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormCheckbox = (fieldRenderProps) => {
  const {
    defaultChecked,
    validationMessage,
    touched,
    onCheckedChanged,
    id,
    valid,
    disabled,
    hint,
    optional,
    label,
    visited,
    modified,
    ...others
  } = fieldRenderProps

  // const [checked, setChecked] = useState(defaultChecked);
  // useEffect(() => {
  //   setChecked(defaultChecked);
  // }, [defaultChecked]);

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  return (
    <FieldWrapper>
      <Checkbox
        ariaDescribedBy={`${hintId} ${errorId}`}
        label={label}
        labelOptional={optional}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormSwitch = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    optional,
    id,
    valid,
    disabled,
    hint,
    // The input props of the Field.
    defaultValue,
    value,
    onChange,
    onFocus,
    onBlur,
    ...others
  } = fieldRenderProps
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  const onValueChange = useCallback(() => {
    // onChange callback expects argument with 'value' property
    onChange({ value: !value })
  }, [onChange, value])

  return (
    <FieldWrapper>
      <Label
        style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        optional={optional}
      >
        {label}
      </Label>
      <Switch
        checked={value}
        defaultChecked={defaultValue}
        onChange={onValueChange}
        ref={editorRef}
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormMaskedTextBox = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    hint,
    optional,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} optional={optional}>
        {label}
      </Label>
      <div className={'k-form-field-wrap'}>
        <MaskedTextBox
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          {...others}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </div>
    </FieldWrapper>
  )
}
export const FormTextArea = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    hint,
    disabled,
    optional,
    defaultText,
  defaultValue,
  value, // pick up current field value supplied by Kendo Form
    x,
    rows,
    ...others
  } = fieldRenderProps

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''

  return (
    <FieldWrapper>
      <Label
        style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
        editorId={id}
        editorValid={valid}
        optional={optional}
      >
        {label}
      </Label>
      <TextArea
  {...others}
  valid={valid}
  id={id}
  rows={rows || 5}
  // Prefer the controlled 'value' if provided; fall back to defaultValue for backwards compatibility
  value={value !== undefined ? value : defaultValue}
  disabled={disabled}
  // Keep defaultValue for any uncontrolled fallbacks (legacy usage in codebase)
  defaultValue={defaultValue !== undefined ? defaultValue : value}
  ariaDescribedBy={`${hintId} ${errorId}`}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormColorPicker = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    ...others
  } = fieldRenderProps
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <ColorPicker
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormSlider = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    data,
    min,
    max,
    ...others
  } = fieldRenderProps
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <Slider
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        disabled={disabled}
        min={min}
        max={max}
        {...others}
      >
        {data.map((value) => (
          <SliderLabel title={value} key={value} position={value}>
            {value.toString()}
          </SliderLabel>
        ))}
      </Slider>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormRangeSlider = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    hint,
    disabled,
    data,
    min,
    max,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid}>
        {label}
      </Label>
      <RangeSlider
        valid={valid}
        id={id}
        disabled={disabled}
        ariaDescribedBy={`${hintId} ${errorId}`}
        min={min}
        max={max}
        {...others}
      >
        {data.map((value) => {
          return (
            <SliderLabel key={value} position={value}>
              {value.toString()}
            </SliderLabel>
          )
        })}
      </RangeSlider>
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormRating = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    hint,
    disabled,
    optional,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid} optional={optional}>
        {label}
      </Label>
      <Rating
        valid={valid}
        id={id}
        disabled={disabled}
        ariaDescribedBy={`${hintId} ${errorId}`}
        {...others}
      />
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormUpload = (fieldRenderProps) => {
  const {
    value,
    id,
    optional,
    label,
    hint,
    validationMessage,
    touched,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  const onChangeHandler = (event) => {
    fieldRenderProps.onChange({
      value: event.newState,
    })
  }
  const onRemoveHandler = (event) => {
    fieldRenderProps.onChange({
      value: event.newState,
    })
  }
  return (
    <FieldWrapper>
      <Label id={labelId} editorId={id} optional={optional}>
        {label}
      </Label>
      <Upload
        id={id}
        autoUpload={false}
        showActionButtons={false}
        multiple={false}
        files={value}
        onAdd={onChangeHandler}
        onRemove={onRemoveHandler}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ariaLabelledBy={labelId}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormDropDownList = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    textField,
    dataItemKey,
    id,
    valid,
    disabled,
    hint,
    defaultText,
    defaultValue,
    wrapperStyle,
    hasButton,
    buttonText,
    buttonAction,
    required,
    onOpen,
    ...others
  } = fieldRenderProps
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Label
          style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
        >
          {label}
        </Label>
        {hasButton && (
          <button
            onClick={buttonAction}
            style={{
              border: 0,
              backgroundColor: 'rgb(148 209 231)',
              color: 'rgb(36 60 69)',
              borderRadius: '1rem',
              fontSize: '9pt',
              padding: '0 1rem 0 1rem',
              marginBottom: '5px',
              fontWeight: '500',
            }}
          >
            {buttonText}
          </button>
        )}
      </div>
      <DropDownList
        textField='text'
        dataItemKey='value'
        placeholder={defaultText}
        ariaLabelledBy={labelId}
        required={required}
        defaultItem={{ value: defaultValue, text: defaultText }}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        onOpen={() => onOpen ? onOpen() : null}
        disabled={disabled}
        popupSettings={{ appendTo: document.body }}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormDropDownListFilter = (fieldRenderProps) => {
  const {
    data,
    setData,
    originalData,
    validationMessage,
    touched,
    label,
    textField,
    dataItemKey,
    id,
    valid,
    disabled,
    hint,
    required,
    defaultText,
    defaultValue,
    wrapperStyle,
    searchDropdown,
    hasButton,
    buttonText,
    buttonAction,
    hasSecondButton,
    secondButtonText,
    secondButtonAction,
    value,
    onOpen,
    ...others
  } = fieldRenderProps

  const editorRef = useRef(null)
  const [filteredData, setFilteredData] = useState(data)

  useEffect(() => {
    setFilteredData(data)
  }, [data])

  const filterChange = async (event) => {
    if (searchDropdown && typeof searchDropdown === 'function') {
      // Server-side filtering via searchDropdown function
      const fetchSearchData = async (search) => {
        await searchDropdown(search)
      }
      fetchSearchData(event.filter.value)
    } else {
      // Client-side filtering when searchDropdown is not provided
      const filterValue = event.filter.value.toLowerCase()
      const textFieldName = textField || 'text'
      
      if (filterValue) {
        const filtered = data.filter((item) => {
          const itemValue = item[textFieldName]
          return itemValue && itemValue.toString().toLowerCase().includes(filterValue)
        })
        setFilteredData(filtered)
      } else {
        setFilteredData(data)
      }
    }
  }

  const handleAction = (event) => {
    event.preventDefault()
    buttonAction()
  }

  const handleSecondAction = (event) => {
    event.preventDefault()
    secondButtonAction()
  }

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <div
      
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Label
          style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
        >
          {label}
        </Label>
        <div>
          {hasButton && (
            <button
              onClick={handleAction}
              style={{
                border: 0,
                backgroundColor: 'rgb(148 209 231)',
                color: 'rgb(36 60 69)',
                borderRadius: '1rem',
                fontSize: '9pt',
                padding: '0 1rem 0 1rem',
                marginBottom: '5px',
                fontWeight: '500',
              }}
            >
              {buttonText}
            </button>
          )}

          {hasSecondButton && (
            <button
              onClick={handleSecondAction}
              style={{
                border: 0,
                backgroundColor: 'rgb(148 209 231)',
                color: 'rgb(36 60 69)',
                borderRadius: '1rem',
                fontSize: '9pt',
                padding: '0 1rem 0 1rem',
                marginBottom: '5px',
                marginLeft: '5px',
                fontWeight: '500',
              }}
            >
              {secondButtonText}
            </button>
          )}
        </div>
      </div>
      <DropDownList
        data={searchDropdown && typeof searchDropdown === 'function' ? data : filteredData}
        filterable={true}
        required={required}
        onFilterChange={filterChange}
        textField={textField || 'text'}
        dataItemKey={dataItemKey || 'value'}
        value={value !== undefined && value !== null ? value : null}
        onOpen={() => onOpen ? onOpen() : null}
        defaultValue={defaultValue}
        defaultText={defaultText}
        defaultItem={{ value: defaultValue, text: defaultText }}
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        disabled={disabled}
        popupSettings={{ appendTo: document.body }}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}

export const FormAutoCompleteFilter = (fieldRenderProps) => {
  const {
    data,
    validationMessage,
    touched,
    label,
    textField,
    dataItemKey,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    ...others
  } = fieldRenderProps
  const [state, setState] = useState({
    data: data.text,
    value: '',
  })
  const filterData = (value) => {
    const data = data
    const filter = {
      value: value,
      operator: 'startswith',
      ignoreCase: true,
    }
    return value ? filterBy(data, filter) : data
  }
  const onChange = (event) => {
    const value = event.value
    setState({
      data: filterData(value),
      value: value,
    })
  }
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <AutoComplete
        data={state.data}
        value={state.value}
        onChange={onChange}
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormAutoComplete = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    textField,
    dataItemKey,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    ...others
  } = fieldRenderProps
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <AutoComplete
        textField='text'
        dataItemKey='value'
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormComboBox = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    ...others
  } = fieldRenderProps
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <ComboBox
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormMultiColumnComboBox = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    ...others
  } = fieldRenderProps
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  const columns = [
    {
      field: 'id',
      header: <span>header</span>,
      width: '100px',
    },
    {
      field: 'name',
      header: 'Name',
      width: '300px',
    },
    {
      field: 'position',
      header: 'Position',
      width: '300px',
    },
  ]
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <MultiColumnComboBox
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        disabled={disabled}
        columns={columns}
        textField={'name'}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormMultiSelect = (fieldRenderProps) => {
  const {
    data,
    setData,
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    defaultValue,
    wrapperStyle,
    value,
    onChange,
    searchDropdown,
    ...others
  } = fieldRenderProps
  const editorRef = useRef(null)
  const [filteredData, setFilteredData] = useState(data)

  useEffect(() => {
    setFilteredData(data)
  }, [data])

  const filterChange = (event) => {
    if (searchDropdown && typeof searchDropdown === 'function') {
      // Server-side filtering via searchDropdown function
      searchDropdown(event.filter.value)
    } else {
      // Client-side filtering when searchDropdown is not provided
      const filterValue = (event.filter.value || '').toLowerCase()
      const textFieldName = others.textField || 'text'

      if (filterValue) {
        setFilteredData(
          (data || []).filter((item) => {
            const itemValue = item[textFieldName]
            return (
              itemValue &&
              itemValue.toString().toLowerCase().includes(filterValue)
            )
          })
        )
      } else {
        setFilteredData(data)
      }
    }
  }

  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  //console.log(defaultValue);
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        style={{ display: "flex" }}
      >
        {label}
      </Label>
      <MultiSelect
        data={searchDropdown && typeof searchDropdown === 'function' ? data : filteredData}
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        dataItemKey='value'
        textField='text'
        value={value || defaultValue || []}
        onChange={onChange}
        ref={editorRef}
        valid={valid}
        filterable={true}
        id={id}
        disabled={disabled}
        popupSettings={{ appendTo: document.body }}
        onFilterChange={filterChange}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormDropDownTree = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    data,
    ...others
  } = fieldRenderProps
  const { value, selectField, expandField, dataItemKey, filter } = others
  const [expanded, setExpanded] = useState([data[0][dataItemKey]])
  const treeData = useMemo(
    () =>
      processTreeData(
        data,
        {
          expanded,
          value,
          filter,
        },
        {
          selectField,
          expandField,
          dataItemKey,
          subItemsField: 'items',
        }
      ),
    [data, expanded, value, filter, selectField, expandField, dataItemKey]
  )
  const onExpandChange = useCallback(
    (event) => setExpanded(expandedState(event.item, dataItemKey, expanded)),
    [expanded, dataItemKey]
  )
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <DropDownTree
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        disabled={disabled}
        data={treeData}
        onExpandChange={onExpandChange}
        dataItemKey={others.dataItemKey}
        textField={others.textField}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormDatePicker = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    hintDirection,
    defaultValue,
    value,
    onChange,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <div className={'k-form-field-wrap'}>
        <DatePicker
          {...others}
          format='dd-MM-yy'
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          value={value ?? defaultValue ?? null}
          onChange={onChange}
          disabled={disabled}
        />
        {showHint && (
          <Hint id={hintId} direction={hintDirection}>
            {hint}
          </Hint>
        )}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </div>
    </FieldWrapper>
  )
}
export const FormDateTimePicker = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <DateTimePicker
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormTimePicker = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <TimePicker
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormDateInput = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <DateInput
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormDateRangePicker = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    ...others
  } = fieldRenderProps
  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''
  return (
    <FieldWrapper style={wrapperStyle}>
      <Label
        id={labelId}
        editorRef={editorRef}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <DateRangePicker
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        disabled={disabled}
        {...others}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
export const FormFloatingNumericTextBox = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    optional,
    value,
    ...others
  } = fieldRenderProps
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  return (
    <FieldWrapper>
      <FloatingLabel
        optional={optional}
        editorValue={value}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        label={label}
      >
        <NumericTextBox
          ariaDescribedBy={`${hintId} ${errorId}`}
          value={value}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
        />
      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}



export const FormAddressLookup = (fieldRenderProps) => {
  const {
    data,
    setData,
    originalData,
    validationMessage,
    touched,
    label,
    textField,
    dataItemKey,
    id,
    valid,
    disabled,
    hint,
    required,
    defaultText,
    defaultValue,
    wrapperStyle,
    searchDropdown,
    hasButton,
    buttonText,
    buttonAction,
    hasSecondButton,
    secondButtonText,
    secondButtonAction,
    value,
    selectedAddress,
    setSelectedAddress,
    gridLayout,
    onChange,
    ...others
  } = fieldRenderProps

  // Track if user is currently filtering/searching
  const [isFiltering, setIsFiltering] = useState(false);

  const filterChange = async (event) => {
    // Clear the selected dropdown value when user starts typing
    if (!isFiltering && onChange) {
      setIsFiltering(true);
      // Clear the selection by passing null/undefined
      onChange({ value: null });
    }

    const fetchSearchData = async (search) => {
      await searchDropdown(search);
    };
  
    fetchSearchData(event.filter.value);
  };

  const handleChange = (e) => {
    setIsFiltering(false); // Reset filtering state when an item is selected
    if (onChange) {
      onChange(e);
    }
  };

  const handleAction = (event) => {
    event.preventDefault()
    buttonAction()
  }

  const handleSecondAction = (event) => {
    event.preventDefault()
    secondButtonAction()
  }


  const editorRef = useRef(null)
  const showValidationMessage = touched && validationMessage
  const showHint = !showValidationMessage && hint
  const hintId = showHint ? `${id}_hint` : ''
  const errorId = showValidationMessage ? `${id}_error` : ''
  const labelId = label ? `${id}_label` : ''

  return (
    <FieldWrapper style={wrapperStyle}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Label
          style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
        >
          {label}
        </Label>
        <div>
          {hasButton && (
            <button
              onClick={handleAction}
              style={{
                border: 0,
                backgroundColor: 'rgb(148 209 231)',
                color: 'rgb(36 60 69)',
                borderRadius: '1rem',
                fontSize: '9pt',
                padding: '0 1rem 0 1rem',
                marginBottom: '5px',
                fontWeight: '500',
              }}
            >
              {buttonText}
            </button>
          )}

          {hasSecondButton && (
            <button
              onClick={handleSecondAction}
              style={{
                border: 0,
                backgroundColor: 'rgb(148 209 231)',
                color: 'rgb(36 60 69)',
                borderRadius: '1rem',
                fontSize: '9pt',
                padding: '0 1rem 0 1rem',
                marginBottom: '5px',
                marginLeft: '5px',
                fontWeight: '500',
              }}
            >
              {secondButtonText}
            </button>
          )}
        </div>
      </div>
      <DropDownList
      style={{marginBottom: "10px"}}
        data={data}
        setData={setData}
        filterable={true}
        required={required}
        onFilterChange={filterChange}
        onChange={handleChange}
        textField='text'
        dataItemKey='value'
        value={value}
        ariaLabelledBy={labelId}
        ariaDescribedBy={`${hintId} ${errorId}`}
        ref={editorRef}
        valid={valid}
        id={id}
        disabled={disabled}
      />
      {
        selectedAddress !== undefined ? (
          <div style={{display: gridLayout ? "grid" : "flex", flexDirection: "column", gap: "10px", gridTemplateColumns: "1fr 1fr 1fr", marginBottom: gridLayout ? "10px" : "0"}}>
            <div>
              <Label
                style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
                id={"line1"}
              >
                Line 1 *
              </Label>
              <TextBox
                onChange={(e) => setSelectedAddress({...selectedAddress, line1: e.target.value})}
                value={selectedAddress.line1 || ''}
                type={"text"}
                id='line1'
                required={required}
                disabled={disabled}
              />          
            </div>
            <div>
              <Label
                style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
                id={"line2"}
              >
                Line 2
              </Label>
              <TextBox
                onChange={(e) => setSelectedAddress({...selectedAddress, line2: e.target.value})}
                value={selectedAddress.line2 || ''}
                type={"text"}
                id='line2'
                disabled={disabled}
              />          
            </div>  
            <div>
              <Label
                style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
                id={"line3"}
              >
                Line 3
              </Label>
              <TextBox
                onChange={(e) => setSelectedAddress({...selectedAddress, line3: e.target.value})}
                value={selectedAddress.line3 || ''}
                type={"text"}
                id='line3'
                disabled={disabled}
              />          
            </div> 
            <div>
              <Label
                style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
                id={"line4"}
              >
                Line 4
              </Label>
              <TextBox
                onChange={(e) => setSelectedAddress({...selectedAddress, line4: e.target.value})}
                value={selectedAddress.line4 || ''}
                type={"text"}
                id='line4'
                disabled={disabled}
              />          
            </div> 
            <div>
              <Label
                style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
                id={"locality"}
              >
                Locality
              </Label>
              <TextBox
                onChange={(e) => setSelectedAddress({...selectedAddress, locality: e.target.value})}
                value={selectedAddress.locality || ''}
                type={"text"}
                id='locality'
                disabled={disabled}
              />          
            </div>
            <div>
              <Label
                style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
                id={"townOrCity"}
              >
                Town or City
              </Label>
              <TextBox
                onChange={(e) => setSelectedAddress({...selectedAddress, townOrCity: e.target.value})}
                value={selectedAddress.townOrCity || ''}
                type={"text"}
                id='townOrCity'
                disabled={disabled}
              />          
            </div>                         
            <div>
              <Label
                style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
                id={"County"}
              >
                County
              </Label>
              <TextBox
                onChange={(e) => setSelectedAddress({...selectedAddress, county: e.target.value})}
                value={selectedAddress.county || ''}
                type={"text"}
                id='County'
                disabled={disabled}
              />          
            </div>  
            <div>
              <Label
                style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
                id={"country"}
              >
                Country
              </Label>
              <TextBox
                onChange={(e) => setSelectedAddress({...selectedAddress, country: e.target.value})}
                value={selectedAddress.country || ''}
                type={"text"}
                id='country'
                disabled={disabled}
              />          
            </div>
            <div>
              <Label
                style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
                id={"postcode"}
              >
                Postcode *
              </Label>
              <TextBox
                onChange={(e) => setSelectedAddress({...selectedAddress, postcode: e.target.value})}
                value={selectedAddress.postcode || ''}
                type={"text"}
                id='postcode'
                required={required}
                disabled={disabled}
              />          
            </div>
            <div>
              <Label
                style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}
                id={"residential"}
              >
                Residential
              </Label>
              <Switch
                checked={selectedAddress.residential || false}
                onChange={(e) => setSelectedAddress({...selectedAddress, residential: e.target.value})}
                id={id}
                disabled={disabled}
              />
            </div>            
          </div>
        ) : null
      }
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  )
}
